import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Table2 } from "lucide-react";
import type { AnalyticsBucket, AnalyticsKpis } from "../../api";
import { fmtBucketLabel } from "../../lib/format";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { NON_ADDITIVE, subLabel, type KpiDescriptor } from "./kpi-descriptors";

/**
 * "Batafsil ko'rsatkichlar" — bosqichma-bosqich ochiladigan KPI jadvali.
 *
 * Sahifaning ASOSIY bloki EMAS: ko'p tashriflarda kartalar yetarli. Ataylab
 * yopiq ochiladi va holat saqlanmaydi; `groupBy` esa TASHQARIDA (sahifada)
 * turadi — u so'rov parametri va davr almashganda tiklanadi, shuning uchun
 * bu yerga ko'chirilsa so'rov qorovuli ishlamay qolardi.
 *
 * Ustunlar `KPI_DESCRIPTORS`dan — kartalar bilan AYNI ro'yxatdan.
 */
export function KpiTableCard({
  data,
  descriptors,
  groupBy,
  options,
  onGroupBy,
  loading,
}: {
  data: AnalyticsKpis | null;
  descriptors: KpiDescriptor[];
  groupBy: AnalyticsBucket;
  options: AnalyticsBucket[];
  onGroupBy: (g: AnalyticsBucket) => void;
  loading: boolean;
}) {
  const { t } = useTranslation("analytics");
  const [open, setOpen] = useState(false);
  // Eng yangi qator yuqorida — loyihadagi sana ro'yxatlari bilan bir xil
  // tartib (`date-ledger.tsx` → buildDateGroups).
  const rows = useMemo(() => [...(data?.buckets ?? [])].reverse(), [data]);

  if (!data && !loading) return null;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Table2 />
          {open ? t("kpiTable.hide") : t("kpiTable.show")}
          {open ? <ChevronUp /> : <ChevronDown />}
        </Button>
      </div>

      {open && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base">{t("kpiTable.title")}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("kpiTable.subtitle")}
                </p>
              </div>
              {/* Bitta variant qolganda ("Bugun" → faqat soat) tab qatori
                  ma'nosiz — ko'rsatmaymiz. */}
              {options.length > 1 && (
                <Tabs value={groupBy} onValueChange={(v) => onGroupBy(v as AnalyticsBucket)}>
                  <TabsList className="overflow-x-auto">
                    {options.map((g) => (
                      <TabsTrigger key={g} value={g}>
                        {t(`kpiTable.groupBy.${g}`)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading && !data ? (
              <div className="p-4">
                <Skeleton className="h-40" />
              </div>
            ) : !data || rows.length === 0 ? (
              <EmptyState title={t("empty.noData")} className="py-8" />
            ) : (
              // Kun kesimida yil davri 365 qator bo'lishi mumkin — balandlik
              // `containerClassName` orqali JADVALNING O'Z skroll qutisiga
              // beriladi. Tashqi `overflow-auto` div bilan o'rash yopishqoq
              // sarlavhani jimgina o'chirardi (sticky eng yaqin skroll
              // qutisiga nisbatan hisoblanadi — u esa ichkarisi bo'lardi).
              <Table containerClassName="max-h-[70vh]">
                <TableHeader className="sticky top-0 z-20 bg-card">
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-card">
                      {t("kpiTable.columns.date")}
                    </TableHead>
                    {descriptors.map((d) => (
                      <TableHead key={d.key} className="text-right">
                        {t(`kpi.${d.labelKey}`)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.date}>
                      <TableCell className="font-medium whitespace-nowrap sticky left-0 bg-card">
                        {fmtBucketLabel(row.date, data.group_by)}
                      </TableCell>
                      {descriptors.map((d) => (
                        <TableCell
                          key={d.key}
                          className="text-right tabular-nums whitespace-nowrap"
                        >
                          {d.format(row.metrics[d.key])}
                          {d.subKey && (
                            <span className="block text-[11px] text-muted-foreground">
                              {subLabel(d, row.metrics)}
                            </span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter className="sticky bottom-0 z-20 bg-muted">
                  <TableRow>
                    <TableCell className="font-semibold sticky left-0 z-10 bg-muted">
                      {t("kpiTable.totals")}
                    </TableCell>
                    {descriptors.map((d) => (
                      <TableCell
                        key={d.key}
                        className="text-right tabular-nums font-semibold whitespace-nowrap"
                      >
                        {d.format(data.current[d.key])}
                        {/* Nisbat va distinct sanoqlar ustun yig'indisi EMAS —
                            butun davr ko'rsatkichi (yulduzcha bilan belgilanadi). */}
                        {NON_ADDITIVE.has(d.key) && (
                          <span
                            className="text-muted-foreground"
                            title={t("kpiTable.periodFigure")}
                          >
                            *
                          </span>
                        )}
                        {d.subKey && (
                          <span className="block text-[11px] font-normal text-muted-foreground">
                            {subLabel(d, data.current)}
                          </span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
